import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useSettingsCache } from "@/shared/cache";
import type { EmailGroupingMode, Settings } from "@/shared/cache/slices/settings.types";
import { Switch } from "@/v2/components/ui/switch";
import { SettingRow } from "@/v2/components/settings/SettingRow";
import { SectionHeading } from "@/v2/components/settings/SectionHeading";
import { NumberStepper } from "@/v2/components/settings/NumberStepper";

function TrackInternalEmailsToggle({
	trackInternal,
	update,
}: {
	trackInternal: boolean;
	update: (patch: Partial<Settings>) => void;
}) {
	return (
		<SettingRow
			title="Track internal emails"
			description="Track emails where all participants are within your organization"
		>
			<Switch
				checked={trackInternal}
				onCheckedChange={(v) =>
					// Force include=false when track goes off — the dependent
					// narrative-scrubbing setting (managed on the Narratives page)
					// is meaningless without tracking, so we clear it here too to
					// avoid a stale `true` lingering in the cache.
					update(
						v
							? { p_track_internal_emails: true }
							: {
									p_track_internal_emails: false,
									p_include_internal_in_narratives: false,
								},
					)
				}
			/>
		</SettingRow>
	);
}

function EmailGroupingToggles({
	mode,
	onChange,
}: {
	mode: EmailGroupingMode;
	onChange: (mode: EmailGroupingMode) => void;
}) {
	const groupDrafts = mode === "combined" || mode === "grouped" || mode === "individual_review";
	const groupReviews = mode === "combined" || mode === "grouped" || mode === "individual_draft";
	const combineAll = mode === "combined";

	const resolveMode = (
		drafts: boolean,
		reviews: boolean,
		combine: boolean,
	): EmailGroupingMode => {
		if (combine) return "combined";
		if (drafts && reviews) return "grouped";
		if (!drafts && reviews) return "individual_draft";
		if (drafts && !reviews) return "individual_review";
		return "individual";
	};

	return (
		<>
			<SettingRow
				title="One entry per thread"
				description="All email activity in a thread — drafting and reviewing — is merged into a single time entry."
			>
				<Switch
					checked={combineAll}
					onCheckedChange={(v) =>
						onChange(v ? "combined" : resolveMode(groupDrafts, groupReviews, false))
					}
				/>
			</SettingRow>
			{!combineAll && (
				<div className="pl-6">
					<SettingRow
						title="One entry per thread for drafts"
						description="Composing multiple emails in the same thread produces one time entry instead of one per email."
					>
						<Switch
							checked={groupDrafts}
							onCheckedChange={(v) =>
								onChange(resolveMode(v, groupReviews, false))
							}
						/>
					</SettingRow>
					<SettingRow
						title="One entry per thread for reviews"
						description="Reading multiple emails in the same thread produces one time entry instead of one per email."
					>
						<Switch
							checked={groupReviews}
							onCheckedChange={(v) =>
								onChange(resolveMode(groupDrafts, v, false))
							}
						/>
					</SettingRow>
				</div>
			)}
		</>
	);
}

export default function Tracking() {
	useDocumentTitle("Tracking");
	const { settings, updateSettings } = useSettingsCache();

	return (
		<div>
			<h2 className="text-lg font-semibold mb-4">Tracking</h2>

			<SectionHeading>General</SectionHeading>
			<SettingRow
				title="Track entries after export"
				description="Entries will continue to be tracked even after they are exported"
			>
				<Switch
					checked={settings.track_after_exported}
					onCheckedChange={(v) =>
						updateSettings({ track_after_exported: v })
					}
				/>
			</SettingRow>
			<SettingRow
				title="Auto-populate last used matter"
				description="When adding an entry, pre-fill the matter from your most recent entry."
			>
				<Switch
					checked={settings.auto_populate_last_used_matter}
					onCheckedChange={(v) =>
						updateSettings({ auto_populate_last_used_matter: v })
					}
				/>
			</SettingRow>
			<SectionHeading>Emails</SectionHeading>
			<SettingRow
				title="Tracking threshold (seconds)"
				description="How long you must spend on an email before it starts being tracked"
			>
				<NumberStepper
					value={settings.time_to_track_emails}
					onChange={(v) => updateSettings({ time_to_track_emails: v })}
					min={5}
					step={5}
				/>
			</SettingRow>
			<TrackInternalEmailsToggle
				trackInternal={settings.p_track_internal_emails}
				update={updateSettings}
			/>
			<EmailGroupingToggles
				mode={settings.email_grouping_mode}
				onChange={(mode) => updateSettings({ email_grouping_mode: mode })}
			/>

			<SectionHeading>Documents</SectionHeading>
			<SettingRow
				title="Tracking threshold (seconds)"
				description="How long you must spend on a document before it starts being tracked"
			>
				<NumberStepper
					value={settings.time_to_track_docs}
					onChange={(v) => updateSettings({ time_to_track_docs: v })}
					min={5}
					step={5}
				/>
			</SettingRow>
			<SectionHeading>Browser</SectionHeading>
			<SettingRow
				title="Tracking threshold (seconds)"
				description="How long you must spend on a page before it starts being tracked"
			>
				<NumberStepper
					value={settings.p_time_to_track_on_browser_s}
					onChange={(v) => updateSettings({ p_time_to_track_on_browser_s: v })}
					min={5}
					step={5}
				/>
			</SettingRow>
			<SettingRow
				title="Session inactivity timeout (minutes)"
				description="How long you must be inactive before browsing is tracked as a new entry"
			>
				<NumberStepper
					value={settings.p_browser_session_inactivity_threshold_m}
					onChange={(v) => updateSettings({ p_browser_session_inactivity_threshold_m: v })}
					min={5}
					step={5}
				/>
			</SettingRow>

			<SectionHeading>Meetings</SectionHeading>
			<SettingRow
				title="Time to track meetings (seconds)"
				description="How long you must spend in a meeting before it starts being tracked"
			>
				<NumberStepper
					value={settings.p_time_to_track_meetings_s}
					onChange={(v) => updateSettings({ p_time_to_track_meetings_s: v })}
					min={5}
					step={5}
				/>
			</SettingRow>
		</div>
	);
}
